import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { pingAPI, pingHelpers } from "@/lib/ping-api";
import { queryKeys, invalidateQueries } from "@/lib/query-keys";
import type {
  CreateInternalChatForm,
  InternalChat,
  InternalChatDetail,
  InternalMessage,
  PaginatedResponse,
} from "@/types";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/stores/authStore";

const getInternalChatListQueries = (queryClient: QueryClient) =>
  queryClient
    .getQueryCache()
    .findAll({
      queryKey: queryKeys.ping.internalChats.all(),
      exact: false,
    })
    .filter((query) => query.queryKey[2] === "list");

const updateInternalChatInCachedLists = (
  queryClient: QueryClient,
  chatId: string,
  updater: (chat: InternalChat) => InternalChat
) => {
  const listQueries = getInternalChatListQueries(queryClient);

  listQueries.forEach((query) => {
    queryClient.setQueryData(
      query.queryKey,
      (oldData: PaginatedResponse<InternalChat> | undefined) => {
        if (!oldData?.results?.some((chat) => chat.id === chatId)) {
          return oldData;
        }

        return {
          ...oldData,
          results: oldData.results.map((chat) =>
            chat.id === chatId ? updater(chat) : chat
          ),
        };
      }
    );
  });
};

// Internal chat mutations
export const useCreateInternalChat = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateInternalChatForm) =>
      pingAPI.internalChats.create(data),
    onSuccess: (chat: InternalChat) => {
      // Invalidate chat lists to refetch with new chat
      queryClient.invalidateQueries(invalidateQueries.internalChats());

      // Add the chat data to cache directly
      queryClient.setQueryData(
        queryKeys.ping.internalChats.detail(chat.id),
        chat
      );

      toast({
        title: "Success",
        description: "Chat created successfully",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to create chat";
      console.error("Failed to create internal chat:", error);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};

// Star internal chat mutation
export const useStarInternalChat = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ chatId }: { chatId: string }) => {
      if (!chatId) {
        return Promise.reject(new Error("Invalid chat ID"));
      }
      return pingAPI.internalChats.star(chatId);
    },
    onMutate: async ({ chatId }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.ping.internalChats.detail(chatId),
      });
      await queryClient.cancelQueries({
        queryKey: queryKeys.ping.internalChats.all(),
        exact: false,
      });

      const previousDetail = queryClient.getQueryData<InternalChatDetail>(
        queryKeys.ping.internalChats.detail(chatId)
      );
      const previousListQueries = queryClient.getQueriesData<
        PaginatedResponse<InternalChat>
      >({
        queryKey: queryKeys.ping.internalChats.all(),
        exact: false,
      });

      const currentIsStarred =
        previousDetail?.is_starred ??
        previousListQueries
          .filter(([queryKey]) => queryKey[2] === "list")
          .flatMap(([, data]) => data?.results ?? [])
          .find((cachedChat) => cachedChat.id === chatId)?.is_starred ??
        false;

      const nextIsStarred = !currentIsStarred;

      queryClient.setQueryData(
        queryKeys.ping.internalChats.detail(chatId),
        (existingChat: InternalChatDetail | undefined) =>
          existingChat
            ? { ...existingChat, is_starred: nextIsStarred }
            : existingChat
      );

      updateInternalChatInCachedLists(queryClient, chatId, (chat) => ({
        ...chat,
        is_starred: nextIsStarred,
      }));

      return { previousDetail, previousListQueries, nextIsStarred };
    },
    onSuccess: (chat: InternalChat | undefined, variables, context) => {
      const serverChat = chat ?? null;

      // Merge with existing detail so partial mutation responses don't blank the conversation pane.
      queryClient.setQueryData(
        queryKeys.ping.internalChats.detail(variables.chatId),
        (existingChat: InternalChatDetail | undefined) =>
          existingChat
            ? {
                ...existingChat,
                ...(serverChat ?? {}),
                is_starred:
                  context?.nextIsStarred ??
                  serverChat?.is_starred ??
                  existingChat.is_starred,
              }
            : serverChat
              ? {
                  ...serverChat,
                  is_starred: context?.nextIsStarred ?? serverChat.is_starred,
                }
              : existingChat
      );

      // Keep cached chat lists in sync without forcing the active conversation to refetch.
      updateInternalChatInCachedLists(
        queryClient,
        variables.chatId,
        (cached) => ({
          ...cached,
          ...(serverChat ?? {}),
          is_starred:
            context?.nextIsStarred ??
            serverChat?.is_starred ??
            cached.is_starred,
        })
      );

      toast({
        title: "Success",
        description:
          context?.nextIsStarred === false ? "Chat unstarred" : "Chat starred",
      });
    },
    onError: (error: any, variables, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(
          queryKeys.ping.internalChats.detail(variables.chatId),
          context.previousDetail
        );
      }

      context?.previousListQueries
        ?.filter(([queryKey]) => queryKey[2] === "list")
        .forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to star chat";
      console.error("Failed to star internal chat:", error);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};

// Toggle internal chat read/unread mutation
export const useMarkInternalChatAsUnread = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ chatId }: { chatId: string }) => {
      if (!chatId) {
        return Promise.reject(new Error("Invalid chat ID"));
      }
      return pingAPI.internalChats.markAsUnread(chatId);
    },
    onMutate: async ({ chatId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.ping.internalChats.all(),
      });

      // Get previous data for rollback
      const previousChats = queryClient.getQueryData(
        queryKeys.ping.internalChats.all()
      );

      // Optimistically toggle is_unread state
      queryClient.setQueryData(
        queryKeys.ping.internalChats.all(),
        (oldData: PaginatedResponse<InternalChat> | undefined) => {
          if (!oldData?.results) return oldData;

          return {
            ...oldData,
            results: oldData.results.map((chat: InternalChat) => {
              if (chat.id === chatId) {
                return {
                  ...chat,
                  is_unread: !chat.is_unread, // Toggle the state
                };
              }
              return chat;
            }),
          };
        }
      );

      return { previousChats };
    },
    onSuccess: (chat: InternalChat, variables) => {
      // Update chat detail cache directly
      queryClient.setQueryData(
        queryKeys.ping.internalChats.detail(variables.chatId),
        chat
      );

      // Refetch chat list to show/hide unread indicator
      queryClient.refetchQueries({
        queryKey: queryKeys.ping.internalChats.all(),
      });

      // Show appropriate message based on the new state
      const message = chat.is_unread
        ? "Chat marked as unread"
        : "Chat marked as read";
      toast({ title: "Success", description: message });
    },
    onError: (error: any, variables, context) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update chat status";
      console.error(
        "Failed to toggle internal chat read/unread status:",
        error
      );
      // Rollback the optimistic update
      if (context?.previousChats) {
        queryClient.setQueryData(
          queryKeys.ping.internalChats.all(),
          context.previousChats
        );
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};

// Mark internal messages as read mutation
export const useMarkInternalMessagesAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      messageId,
      chatId,
    }: {
      messageId: string;
      chatId: string;
    }) => pingAPI.internalMessages.markAsRead(messageId),
    onMutate: async ({ messageId, chatId }) => {
      // Cancel any outgoing refetches to avoid conflicts
      await queryClient.cancelQueries({
        queryKey: queryKeys.ping.internalChats.all(),
      });

      // Get previous data for rollback
      const previousChats = queryClient.getQueryData(
        queryKeys.ping.internalChats.all()
      );

      // Optimistically update chat list to set unread count to 0 and is_unread to false
      queryClient.setQueryData(
        queryKeys.ping.internalChats.all(),
        (oldData: PaginatedResponse<InternalChat> | undefined) => {
          if (!oldData?.results) return oldData;

          return {
            ...oldData,
            results: oldData.results.map((chat: InternalChat) => {
              if (chat.id === chatId) {
                return {
                  ...chat,
                  unread_count: 0,
                  is_unread: false,
                };
              }
              return chat;
            }),
          };
        }
      );

      return { previousChats };
    },
    onSuccess: (response, { messageId, chatId }) => {
      // IMMEDIATELY refetch chats to ensure server sync
      queryClient.refetchQueries({
        queryKey: queryKeys.ping.internalChats.all(),
      });

      // Invalidate messages to update read status
      queryClient.invalidateQueries({
        queryKey: queryKeys.ping.internalMessages.all(),
      });

      // Also invalidate the specific chat detail
      queryClient.invalidateQueries({
        queryKey: queryKeys.ping.internalChats.detail(chatId),
      });
    },
    onError: (error, { messageId, chatId }, context) => {
      console.error(
        `Failed to mark internal message ${messageId} as read for chat ${chatId}:`,
        error
      );
      // Rollback the optimistic update
      if (context?.previousChats) {
        queryClient.setQueryData(
          queryKeys.ping.internalChats.all(),
          context.previousChats
        );
      }
    },
  });
};

// Send internal text message mutation
export const useSendInternalTextMessage = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: ({ chatId, text }: { chatId: string; text: string }) =>
      pingHelpers.sendInternalTextMessage(chatId, text),
    onMutate: async (variables) => {
      const { chatId, text } = variables;

      // Cancel ALL outgoing refetches for this chat's infinite queries (all pageSizes)
      await queryClient.cancelQueries({
        queryKey: queryKeys.ping.internalMessages.infinite(chatId),
        exact: false,
      });

      // Snapshot ALL infinite query data for rollback
      const previousQueries = queryClient.getQueriesData({
        queryKey: queryKeys.ping.internalMessages.infinite(chatId),
        exact: false,
      });

      // Create optimistic message
      const optimisticMessage: InternalMessage = {
        id: `temp-${Date.now()}`,
        chat: chatId,
        sender: user?.id ? parseInt(user.id) : 0,
        sender_name: "You",
        sender_user_type: user?.role || "",
        message_type: "text",
        text_content: text,
        media_file: null,
        media_thumbnail: null,
        created_at: new Date().toISOString(),
        is_read_by_participant_1: false,
        is_read_by_participant_2: false,
        is_read: false,
      };

      // Optimistically update ALL infinite query variants
      const queries = queryClient.getQueryCache().findAll({
        queryKey: queryKeys.ping.internalMessages.infinite(chatId),
        exact: false,
      });

      queries.forEach((query) => {
        queryClient.setQueryData(query.queryKey, (old: any) => {
          if (!old) return old;

          const newPages = [...old.pages];
          if (newPages.length > 0) {
            newPages[0] = {
              ...newPages[0],
              results: [optimisticMessage, ...(newPages[0].results || [])],
              count: (newPages[0].count || 0) + 1,
            };
          } else {
            newPages.push({
              count: 1,
              next: null,
              previous: null,
              results: [optimisticMessage],
            });
          }

          return {
            ...old,
            pages: newPages,
            pageParams: old.pageParams,
          };
        });
      });

      return { previousQueries };
    },
    onSuccess: async (message, variables) => {
      // IMMEDIATELY refetch (not just invalidate) to ensure latest server data
      await queryClient.refetchQueries({
        queryKey: queryKeys.ping.internalMessages.infinite(variables.chatId),
        exact: false,
      });

      // Refetch chat list
      await queryClient.refetchQueries({
        queryKey: queryKeys.ping.internalChats.all(),
      });
    },
    onError: (error, variables, context) => {
      // Rollback ALL optimistic updates
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error("Failed to send internal text message:", error);
    },
  });
};

// Send internal image message mutation
export const useSendInternalImageMessage = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: ({
      chatId,
      imageFile,
      textContent,
    }: {
      chatId: string;
      imageFile: File;
      textContent?: string;
    }) => {
      return pingAPI.internalMessages.create({
        chat: chatId,
        message_type: "image",
        text_content: textContent,
        media_file: imageFile,
      });
    },
    onMutate: async (variables) => {
      const { chatId, imageFile, textContent } = variables;

      // Cancel ALL outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.ping.internalMessages.infinite(chatId),
        exact: false,
      });

      // Snapshot ALL infinite query data
      const previousQueries = queryClient.getQueriesData({
        queryKey: queryKeys.ping.internalMessages.infinite(chatId),
        exact: false,
      });

      // Create optimistic message
      const optimisticMessage: InternalMessage = {
        id: `temp-${Date.now()}`,
        chat: chatId,
        sender: user?.id ? parseInt(user.id) : 0,
        sender_name: "You",
        sender_user_type: user?.role || "",
        message_type: "image",
        text_content: textContent || null,
        media_file: URL.createObjectURL(imageFile),
        media_thumbnail: null,
        created_at: new Date().toISOString(),
        is_read_by_participant_1: false,
        is_read_by_participant_2: false,
        is_read: false,
      };

      // Update ALL infinite query variants
      const queries = queryClient.getQueryCache().findAll({
        queryKey: queryKeys.ping.internalMessages.infinite(chatId),
        exact: false,
      });

      queries.forEach((query) => {
        queryClient.setQueryData(query.queryKey, (old: any) => {
          if (!old) return old;

          const newPages = [...old.pages];
          if (newPages.length > 0) {
            newPages[0] = {
              ...newPages[0],
              results: [optimisticMessage, ...(newPages[0].results || [])],
              count: (newPages[0].count || 0) + 1,
            };
          } else {
            newPages.push({
              count: 1,
              next: null,
              previous: null,
              results: [optimisticMessage],
            });
          }

          return {
            ...old,
            pages: newPages,
            pageParams: old.pageParams,
          };
        });
      });

      return { previousQueries };
    },
    onSuccess: async (message, variables) => {
      // IMMEDIATELY refetch to get latest server data
      await queryClient.refetchQueries({
        queryKey: queryKeys.ping.internalMessages.infinite(variables.chatId),
        exact: false,
      });
      await queryClient.refetchQueries({
        queryKey: queryKeys.ping.internalChats.all(),
      });
    },
    onError: (error, variables, context) => {
      // Rollback ALL optimistic updates
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error("Failed to send internal image message:", error);
    },
  });
};

// Send internal document message mutation
export const useSendInternalDocumentMessage = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: ({
      chatId,
      documentFile,
      textContent,
    }: {
      chatId: string;
      documentFile: File;
      textContent?: string;
    }) => {
      return pingAPI.internalMessages.create({
        chat: chatId,
        message_type: "document",
        text_content: textContent,
        media_file: documentFile,
      });
    },
    onMutate: async (variables) => {
      const { chatId, documentFile, textContent } = variables;

      // Cancel ALL outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.ping.internalMessages.infinite(chatId),
        exact: false,
      });

      // Snapshot ALL infinite query data
      const previousQueries = queryClient.getQueriesData({
        queryKey: queryKeys.ping.internalMessages.infinite(chatId),
        exact: false,
      });

      // Create optimistic message
      const optimisticMessage: InternalMessage = {
        id: `temp-${Date.now()}`,
        chat: chatId,
        sender: user?.id ? parseInt(user.id) : 0,
        sender_name: "You",
        sender_user_type: user?.role || "",
        message_type: "document",
        text_content: textContent || null,
        media_file: URL.createObjectURL(documentFile),
        media_thumbnail: null,
        created_at: new Date().toISOString(),
        is_read_by_participant_1: false,
        is_read_by_participant_2: false,
        is_read: false,
      };

      // Update ALL infinite query variants
      const queries = queryClient.getQueryCache().findAll({
        queryKey: queryKeys.ping.internalMessages.infinite(chatId),
        exact: false,
      });

      queries.forEach((query) => {
        queryClient.setQueryData(query.queryKey, (old: any) => {
          if (!old) return old;

          const newPages = [...old.pages];
          if (newPages.length > 0) {
            newPages[0] = {
              ...newPages[0],
              results: [optimisticMessage, ...(newPages[0].results || [])],
              count: (newPages[0].count || 0) + 1,
            };
          } else {
            newPages.push({
              count: 1,
              next: null,
              previous: null,
              results: [optimisticMessage],
            });
          }

          return {
            ...old,
            pages: newPages,
            pageParams: old.pageParams,
          };
        });
      });

      return { previousQueries };
    },
    onSuccess: async (message, variables) => {
      // IMMEDIATELY refetch to get latest server data
      await queryClient.refetchQueries({
        queryKey: queryKeys.ping.internalMessages.infinite(variables.chatId),
        exact: false,
      });
      await queryClient.refetchQueries({
        queryKey: queryKeys.ping.internalChats.all(),
      });
    },
    onError: (error, variables, context) => {
      // Rollback ALL optimistic updates
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error("Failed to send internal document message:", error);
    },
  });
};
